import React from "react";
import { makeStyles } from "@mui/styles";
import {
  Paper,
  Box,
  Typography,
  Avatar,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableFooter,
  TablePagination,
  Button,
} from "@mui/material";
import Skeleton from "@mui/lab/Skeleton";
import { useQuery } from "@tanstack/react-query";
import { BrandedAppStatus } from "@growth-machine-llc/stridist-api-client";
import { useNavigate, useLocation } from "react-router-dom";
import AdminService from "../../../services/AdminService";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { Check, ArrowRight } from "lucide-react";
import { AddBrandedAppDialog } from "./AddBrandedAppDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    borderRadius: theme.spacing(1.5),
    boxShadow: theme.shadows[4],
    marginBottom: theme.spacing(3),
    overflowX: "auto",
  },
  table: {
    minWidth: 650,
  },
  coachCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  actionsCell: {
    textAlign: "center",
  },
}));

export const ADMIN_BRANDED_APPS_QUERY_KEY = "admin-branded-apps";

export const BrandedAppsTable: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const [queryParams, setQueryParams] = useQueryParams({
    page: "0",
    perPage: "30",
  });
  const page = parseInt(queryParams.page, 10) || 0;
  const rowsPerPage = parseInt(queryParams.perPage, 10) || 30;

  const { data, isFetching } = useQuery({
    queryKey: [ADMIN_BRANDED_APPS_QUERY_KEY, queryParams],
    queryFn: () =>
      AdminService.getBrandedApps({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
      }),
  });

  const handleChangePage = (_: any, newPage: number) =>
    setQueryParams({ page: newPage.toString() });
  const handleChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement>
  ) =>
    setQueryParams({ perPage: e.target.value, page: "0" });

  const items = data?.items || [];
  const totalCount = data?.totalCount || 0;
  const emptyRows = Array.from({ length: rowsPerPage });

  const [addDialogOpen, setAddDialogOpen] = React.useState(false);

  return (
    <Paper className={classes.root}>
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Branded Apps ({totalCount})
          </Typography>
          <Button variant="contained" onClick={() => setAddDialogOpen(true)}>
            Add Branded App
          </Button>
        </Box>

        <Table className={classes.table} aria-label="Branded Apps Table">
          {isFetching ? (
            <>
              <TableHead>
                <TableRow>
                  {Array.from({ length: 7 }).map((_, idx) => (
                    <TableCell key={idx}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {emptyRows.map((_, rowIdx) => (
                  <TableRow key={rowIdx} sx={{ height: 56 }}>
                    {Array.from({ length: 7 }).map((_, cellIdx) => (
                      <TableCell key={cellIdx} align="center">
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </>
          ) : (
            <>
              <TableHead>
                <TableRow>
                  <TableCell>Coach</TableCell>
                  <TableCell>App</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Brand & Stores Access</TableCell>
                  <TableCell align="center">App Store</TableCell>
                  <TableCell align="center">Google Play</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((app) => (
                  <TableRow key={app.workspaceId + app.name} hover>
                    <TableCell>
                      <Box className={classes.coachCell}>
                        <Avatar src={app.coach?.photoUrl} />
                        <Box>
                          <Typography noWrap>{app.coach?.displayName}</Typography>
                          <Typography variant="caption" noWrap>
                            {app.coach?.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {app.logoUrl && (
                          <img
                            src={app.logoUrl}
                            alt={app.name}
                            width={32}
                            height={32}
                            style={{ borderRadius: 4 }}
                          />
                        )}
                        <Typography noWrap>{app.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography>{app.status}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        {[
                          app.brandCompletedMark,
                          app.appleStoreAccessMark,
                          app.googlePlayAccessMark,
                        ]
                          .filter(Boolean)
                          .map((_, idx) => (
                            <Check key={idx} size={16} />
                          ))}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {app.appleStoreUrl ? (
                        <Button
                          component="a"
                          href={app.appleStoreUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                        >
                          View
                        </Button>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {app.googlePlayUrl ? (
                        <Button
                          component="a"
                          href={app.googlePlayUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                        >
                          View
                        </Button>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className={classes.actionsCell}>
                      <IconButton
                        onClick={() =>
                          navigate(`${location.pathname}/${app.workspaceId}`)
                        }
                      >
                        <ArrowRight size={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {items.length === 0 && (
                  <TableRow>
                    <TableCell align="center" colSpan={7}>
                      No apps found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </>
          )}
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                showFirstButton
                showLastButton
                colSpan={7}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Box>

      <AddBrandedAppDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
      />
    </Paper>
  );
};
